<template>
  <b-modal
    ref="modal"
    hide-footer
    centered
    title="Edit Kanban options"
    @shown="modalShown = true"
    @hidden="modalShown = false"
  >
    <b-form @submit.prevent="save">
      <b-form-group
        label="Lanes storage:"
      >
        <b-form-select
          v-model.trim="$v.lanesStorage.$model"
          :options="availableDataGrids"
          :class="{ 'is-invalid': $v.lanesStorage.$error }"
          :disabled="inProcess"
        />
        <b-form-invalid-feedback
          v-if="!$v.lanesStorage.required"
        >
          Lanes storage is required
        </b-form-invalid-feedback>
      </b-form-group>
      <div v-if="lanesStorage">
        <b-form-group
          label="Lane column:"
        >
          <b-form-select
            v-model.trim="$v.laneCol.$model"
            :options="availableLanesStorageColumns"
            :class="{ 'is-invalid': $v.laneCol.$error }"
            :disabled="inProcess"
          />
          <b-form-invalid-feedback
            v-if="!$v.laneCol.required"
          >
            Lane column is required
          </b-form-invalid-feedback>
        </b-form-group>
      </div>
      <b-form-group
        label="Tasks storage:"
      >
        <b-form-select
          v-model.trim="$v.itemsStorage.$model"
          :options="availableDataGrids"
          :class="{ 'is-invalid': $v.itemsStorage.$error }"
          :disabled="inProcess"
        />
        <b-form-invalid-feedback
          v-if="!$v.itemsStorage.required"
        >
          Tasks storage is required
        </b-form-invalid-feedback>
      </b-form-group>
      <div v-if="itemsStorage">
        <b-form-group
          label="Title column:"
        >
          <b-form-select
            v-model.trim="$v.titleCol.$model"
            :options="availableItemsStorageColumns('titleCol')"
            :class="{ 'is-invalid': $v.titleCol.$error }"
            :disabled="inProcess"
          />
          <b-form-invalid-feedback
            v-if="!$v.titleCol.required"
          >
            Title column is required
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
          label="Status column:"
        >
          <b-form-select
            v-model.trim="$v.statusCol.$model"
            :options="availableItemsStorageColumns('statusCol')"
            :class="{ 'is-invalid': $v.statusCol.$error }"
            :disabled="inProcess"
          />
          <b-form-invalid-feedback
            v-if="!$v.statusCol.required"
          >
            Status column is required
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
          label="Description column:"
        >
          <b-form-select
            v-model.trim="$v.descriptionCol.$model"
            :options="availableItemsStorageColumns('descriptionCol')"
            :class="{ 'is-invalid': $v.descriptionCol.$error }"
            :disabled="inProcess"
          />
          <b-form-invalid-feedback
            v-if="!$v.descriptionCol.required"
          >
            Description column is required
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
          label="Sort index column:"
        >
          <b-form-select
            v-model.trim="$v.sortIndexCol.$model"
            :options="availableItemsStorageColumns('sortIndexCol')"
            :class="{ 'is-invalid': $v.sortIndexCol.$error }"
            :disabled="inProcess"
          />
          <b-form-invalid-feedback
            v-if="!$v.sortIndexCol.required"
          >
            Sort index column is required
          </b-form-invalid-feedback>
        </b-form-group>
      </div>
      <b-form-group>
        <template slot="label">
          <span>Template code:</span>
          <icon
            name="question-circle"
            style="vertical-align: -3px;"
            v-b-tooltip="'Use `{{ item }}` variable to access data'"
          />
        </template>
        
        <div class="templateEditor">
          <codemirror
            v-if="modalShown"
            :value="template"
            :options="cmTemplateOptions"
            @input="onCmTemplateCodeChange"
            @keydown.ctrl.83.native.prevent="save"
          />
        </div>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox
          v-model="addOnChangeScript"
        >
          add on change script
        </b-form-checkbox>
      </b-form-group>
      <b-form-group
        v-if="addOnChangeScript"
        label="On change code:"
      >
        <div class="templateEditor">
          <codemirror
            v-if="modalShown"
            :value="onChangeScript"
            :options="cmScriptOptions"
            @input="onCmScriptCodeChange"
            @keydown.ctrl.83.native.prevent="save"
          />
        </div>
      </b-form-group>
      <b-form-group
        label="Workflow (Optional):"
      >
        <b-form-select
          :options="workflows"
          v-model="workflow"
          :disabled="inProcess"
        />
      </b-form-group>
      <div>
        <b-button
          variant="success"
          type="submit"
          >
          Save
        </b-button>
      </div>
    </b-form>
  </b-modal>
</template>

<script>
import { mapState } from 'vuex'
import { required } from 'vuelidate/lib/validators'
// services
import db from '@/services/db'
// helpers
import constants from '@/helpers/constants'

export default {
  props: ['options', 'kanbanId'],
  data () {
    return {
      modalShown: false,
      inProcess: false,
      lanesStorage: this.options.lanesStorage,
      laneCol: this.options.laneCol,
      itemsStorage: this.options.itemsStorage,
      titleCol: this.options.titleCol,
      statusCol: this.options.statusCol,
      descriptionCol: this.options.descriptionCol,
      sortIndexCol: this.options.sortIndexCol,
      template: this.options.template,
      addOnChangeScript: this.options.onChangeScript ? true : false,
      onChangeScript: this.options.onChangeScript || '',
      cmTemplateOptions: constants.cmTemplateOptions,
      cmScriptOptions: constants.cmScriptOptions,
      workflow: this.options.workflow || null,
      workflows:[]
    }
  },
  watch: {
    lanesStorage () {
      this.laneCol = null
    },
    itemsStorage () {
      this.titleCol = null
      this.statusCol = null
      this.descriptionCol = null
      this.sortIndexCol = null
      this.workflow = null
    }
  },
  computed: {
    ...mapState([
      'dataGrid_options',
      'currentTab'
    ]),
    availableDataGrids () {
      const dataGrids = []

      for (let gridId in this.dataGrid_options) {
        dataGrids.push({ value: gridId, text: this.dataGrid_options[gridId].title })
      }

      return dataGrids
    },
    availableLanesStorageColumns () {
      const columns = []

      const meta = this.dataGrid_options[this.lanesStorage].meta

      for (let colKey in meta) {
        const item = meta[colKey]

        columns.push({ text: item.displayName, value: colKey })
      }

      return columns
    }
  },
  methods: {
    availableItemsStorageColumns (currentCol) {
      const columns = [
        { text: 'NOT SELECTED', value: null }
      ]

      const meta = this.dataGrid_options[this.itemsStorage].meta

      for (let colKey in meta) {
        const item = meta[colKey]

        switch (colKey) {
          case this.titleCol: if (currentCol !== 'titleCol') continue
            break
          case this.statusCol: if (currentCol !== 'statusCol') continue
            break
          case this.descriptionCol: if (currentCol !== 'descriptionCol') continue
            break
          case this.sortIndexCol: if (currentCol !== 'sortIndexCol') continue
            break
        }

        columns.push({ text: item.displayName, value: colKey })
      }

      return columns
    },
    open () {
      if (this.$refs.modal) this.$refs.modal.show()
    },
    close () {
      if (this.$refs.modal) this.$refs.modal.hide()
    },
    save () {
      if (this.inProcess) return
      this.$v.$touch()
      if (this.$v.$invalid) return
      this.inProcess = true

      const data = {
        lanesStorage: this.lanesStorage,
        laneCol: this.laneCol,
        itemsStorage: this.itemsStorage,
        titleCol: this.titleCol,
        statusCol: this.statusCol,
        descriptionCol: this.descriptionCol,
        sortIndexCol: this.sortIndexCol,
        template: this.template,
        workflow: this.workflow
      }

      if (this.addOnChangeScript) data.onChangeScript = this.onChangeScript

      this.$store.commit('addProcess', {
        process: 'newKanban',
        message: 'Updating'
      })

      db.kanban.saveOptions(this.kanbanId, data, () => {
        this.$store.commit('deleteProcess', 'newKanban')
        this.inProcess = false
        this.$nextTick(() => {
          this.$v.$reset()
        })
        this.close()
      })
    },
    onCmTemplateCodeChange (code) {
      this.template = code
    },
    onCmScriptCodeChange (code) {
      this.onChangeScript = code
    }
  },
  validations: {
    itemsStorage: {
      required
    },
    laneCol: {
      required
    },
    lanesStorage: {
      required
    },
    titleCol: {
      required
    },
    statusCol: {
      required
    },
    descriptionCol: {
      required
    },
    sortIndexCol: {
      required
    }
  },
  created () {
    db.workflow.read((items) => {
      var workflows = []

      for (let key in items) {
        workflows.push({ text: items[key].name, value: key })
      }

      this.workflows = workflows
    })
  }
}
</script>
